import React, { useState, useEffect } from "react";
import { TenantLogo } from "@/components/TenantLogo";
import { tenantHint, setTenantHint } from "@/lib/tenant";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

/**
 * Brand palette (from we-academy.de):
 *   primary teal   #00CCC9
 *   primary light  #26E0DD
 *   primary dark   #009F9C
 *   accent yellow  #FFD100
 *   accent mint    #A8E6D9
 *   foreground     #0A0A0A
 *   background     #FFFFFF
 *
 * No pinks/purples/oranges. Every colored surface uses one of the tokens above.
 */

function TenantHero({ brand }) {
  return (
    <div className="relative hidden lg:flex flex-col justify-end p-14 overflow-hidden text-white" style={{ background: brand?.brand_color || "#0f172a" }} data-testid="login-tenant-hero">
      <div className="absolute top-8 left-10 bg-white/95 px-4 py-3"><TenantLogo className="h-9 w-auto" testId="login-logo-hero" /></div>
      <div className="text-[10px] font-bold uppercase tracking-overline text-white/70 mb-3">Portal</div>
      <div className="font-heading text-5xl font-light tracking-tighter leading-[0.95]">{brand?.name || "Portal"}</div>
      <div className="mt-4 text-sm text-white/80 max-w-md">Coaching, Weiterbildung und Abrechnung an einem Ort.</div>
    </div>
  );
}

export default function LoginPage() {
  const { brand } = useAuth();
  const isTenant = !!tenantHint() || !!brand?.slug;
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (params.get("error") === "google") {
      const msg = params.get("msg");
      setError(msg || "Google-Anmeldung fehlgeschlagen. Bitte prüfe, ob deine Google-E-Mail vom Admin eingeladen wurde.");
    } else if (params.get("error") === "google_not_invited") {
      setError("Diese Google-E-Mail wurde nicht eingeladen. Bitte beim Admin nachfragen.");
    }
  }, [params]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      const target =
        u?.role === "client" ? "/mein-bereich"
        : u?.role === "participant" ? "/weiterbildung/mein-bereich"
        : "/dashboard";
      navigate(target, { replace: true });
    } catch (e2) {
      setError(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] bg-white relative overflow-hidden" data-testid="login-page">
      {isTenant ? <TenantHero brand={brand} /> : (
      <div className="relative hidden lg:block overflow-hidden bg-white">
        {/* Grain overlay */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.10] mix-blend-multiply z-30"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.5'/></svg>\")",
          }}
        />

        {/* Soft branded glow orbs (teal + yellow + mint only) */}
        <div className="absolute -top-24 -left-24 w-[560px] h-[560px] rounded-full blur-[140px] opacity-60 bg-[#00CCC9]" />
        <div className="absolute top-1/3 -right-32 w-[560px] h-[560px] rounded-full blur-[150px] opacity-40 bg-[#FFD100]" />
        <div className="absolute bottom-[-140px] left-1/4 w-[520px] h-[520px] rounded-full blur-[130px] opacity-55 bg-[#A8E6D9]" />

        {/* Bento grid */}
        <div className="relative z-10 h-full grid grid-cols-6 grid-rows-6 gap-3 pt-20 px-8 pb-8">

          {/* Big card: Design mit KI — teal gradient */}
          <a
            href="https://we-academy.de/weiterbildung/design-mit-ki"
            target="_blank" rel="noopener noreferrer"
            className="col-span-4 row-span-3 relative overflow-hidden border border-[#00CCC9]/30 p-6 flex flex-col justify-between hover:scale-[1.01] transition-transform group"
            data-testid="hero-card-design"
            style={{ background: "linear-gradient(135deg, #26E0DD 0%, #00CCC9 45%, #009F9C 100%)" }}
          >
            <div className="absolute inset-0 opacity-30" style={{
              backgroundImage:
                "radial-gradient(circle at 20% 30%, rgba(255,255,255,0.4) 0%, transparent 40%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.25) 0%, transparent 40%)",
            }} />
            <div className="relative">
              <div className="text-[10px] font-bold uppercase tracking-overline text-white/85">
                Weiterbildung · Modul 03
              </div>
              <div className="font-heading text-4xl xl:text-6xl font-light tracking-tighter text-white leading-[0.95] mt-2">
                Design<br/>mit&nbsp;KI.
              </div>
            </div>
            <div className="relative flex items-end justify-between">
              <div className="flex gap-1.5 flex-wrap max-w-[70%]">
                <BrandTag>Midjourney</BrandTag>
                <BrandTag>Firefly</BrandTag>
                <BrandTag>Portfolio</BrandTag>
              </div>
              <ArrowUpRight />
            </div>
          </a>

          {/* 3D + Unreal — black card with teal glow */}
          <a
            href="https://we-academy.de/weiterbildung/3d-visualisierung"
            target="_blank" rel="noopener noreferrer"
            className="col-span-2 row-span-3 relative overflow-hidden border border-[#00CCC9]/25 bg-[#0A0A0A] p-5 flex flex-col justify-between hover:scale-[1.01] transition-transform group"
            data-testid="hero-card-3d"
          >
            {/* subtle mono teal aura */}
            <div className="absolute inset-0 opacity-55" style={{
              background: "radial-gradient(circle at 55% 50%, rgba(0,204,201,0.75) 0%, rgba(0,159,156,0.35) 45%, transparent 75%)",
              filter: "blur(20px)",
            }} />
            <div className="absolute inset-6 border border-[#00CCC9]/25" style={{ transform: "rotate(6deg)" }} />
            <div className="absolute inset-10 border border-[#00CCC9]/15" style={{ transform: "rotate(-6deg)" }} />
            <div className="relative">
              <div className="text-[10px] font-bold uppercase tracking-overline text-[#26E0DD]">
                Modul 04
              </div>
              <div className="font-heading text-3xl xl:text-4xl font-light tracking-tighter text-white leading-[0.95] mt-2">
                3D&nbsp;+ Unreal
              </div>
            </div>
            <div className="relative flex items-end justify-between">
              <span className="text-[10px] font-mono text-white/75">Blender · Unreal</span>
              <ArrowUpRight />
            </div>
          </a>

          {/* Content Marketing — yellow */}
          <a
            href="https://we-academy.de/weiterbildung/content-marketing-mit-ki"
            target="_blank" rel="noopener noreferrer"
            className="col-span-3 row-span-3 relative overflow-hidden border border-[#FFD100]/40 p-5 flex flex-col justify-between hover:scale-[1.01] transition-transform text-[#0A0A0A]"
            data-testid="hero-card-marketing"
            style={{ background: "linear-gradient(135deg, #FFE566 0%, #FFD100 60%, #E6BB00 100%)" }}
          >
            <div className="absolute inset-0 opacity-30" style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, rgba(0,0,0,0.06) 0 8px, transparent 8px 16px)",
            }} />
            <div className="relative">
              <div className="text-[10px] font-bold uppercase tracking-overline opacity-70">
                Modul 02
              </div>
              <div className="font-heading text-3xl xl:text-5xl font-light tracking-tighter leading-[0.95] mt-2">
                Content<br/>Marketing.
              </div>
            </div>
            <div className="relative flex items-end justify-between">
              <span className="text-[10px] font-mono opacity-80">SEO · Social · KI-Workflows</span>
              <ArrowUpRight dark />
            </div>
          </a>

          {/* Jobcoaching — mint */}
          <a
            href="https://we-academy.de/coaching/jobcoaching/orientierung-profiling"
            target="_blank" rel="noopener noreferrer"
            className="col-span-3 row-span-3 relative overflow-hidden border border-[#A8E6D9]/60 p-5 flex flex-col justify-between hover:scale-[1.01] transition-transform text-[#0A0A0A]"
            data-testid="hero-card-coaching"
            style={{ background: "linear-gradient(135deg, #C9F0E5 0%, #A8E6D9 55%, #7ED9C4 100%)" }}
          >
            <div className="absolute inset-0" style={{
              background:
                "radial-gradient(circle at 90% 10%, rgba(255,255,255,0.5), transparent 40%), radial-gradient(circle at 10% 90%, rgba(0,204,201,0.25), transparent 45%)",
            }} />
            <div className="relative">
              <div className="text-[10px] font-bold uppercase tracking-overline opacity-70">
                Coaching · AVGS
              </div>
              <div className="font-heading text-3xl xl:text-5xl font-light tracking-tighter leading-[0.95] mt-2">
                Job-<br/>coaching.
              </div>
            </div>
            <div className="relative flex items-end justify-between">
              <div className="flex gap-1.5 flex-wrap">
                <DarkTag>Portfolio</DarkTag>
                <DarkTag>LinkedIn</DarkTag>
                <DarkTag>Bewerbung</DarkTag>
              </div>
              <ArrowUpRight dark />
            </div>
          </a>

          {/* Bottom stats strip */}
          <div className="col-span-6 row-span-1 grid grid-cols-4 gap-3">
            <StatCard k="500+" v="Absolvent:innen" />
            <StatCard k="100%" v="AVGS · Bildungsgutschein" />
            <StatCard k="4.9★" v="Google Bewertung" />
            <StatCard k="AZAV" v="zertifiziert" accent />
          </div>
        </div>

        {/* Top-left branding lockup */}
        <div className="absolute top-6 left-8 z-20 flex items-center gap-3">
          <TenantLogo className="h-9 w-auto" testId="login-logo-hero" />
          <div className="pl-3 border-l border-[#0A0A0A]/25">
            <div className="text-[9px] font-bold uppercase tracking-overline text-[#0A0A0A]/70">Portal</div>
            <div className="text-[11px] font-mono text-[#0A0A0A]/85">of Arts &amp; Tech</div>
          </div>
        </div>
      </div>
      )}

      {/* ============ Right: login card ============ */}
      <div className="relative flex items-center justify-center p-6 lg:p-12 bg-white">
        {!isTenant && <MobileHero />}

        <div className="w-full max-w-md relative z-10" data-testid="login-form-wrap">
          {/* Corner accents — brand only (teal + yellow) */}
          <div className="absolute -top-8 -right-4 w-28 h-28 bg-[#FFD100] blur-3xl opacity-25 pointer-events-none" />
          <div className="absolute -bottom-8 -left-4 w-28 h-28 bg-[#00CCC9] blur-3xl opacity-25 pointer-events-none" />

          <TenantLogo className="w-40 h-auto mb-8 lg:hidden" testId="login-logo-mobile" />

          <div className="inline-flex items-center gap-2 mb-5">
            <span className="w-2 h-2 rounded-full bg-[#00CCC9] animate-pulse" />
            <span className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Portal · Live</span>
          </div>
          <h1 className="font-heading text-5xl font-light tracking-tighter text-[#0A0A0A] leading-[0.95]">
            Willkommen<br/>
            <span className="text-[#00CCC9]" style={isTenant ? { color: brand?.brand_color || "#0f172a" } : undefined}>zurück.</span>
          </h1>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="auth-form">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-2 block">E-Mail</label>
              <input
                data-testid="auth-email-input"
                type="email"
                className="input-tech"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder={isTenant ? "name@beispiel.de" : "name@we-academy.de"}
              />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-2 block">Passwort</label>
              <input
                data-testid="auth-password-input"
                type="password"
                className="input-tech"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                autoComplete="current-password"
                placeholder="••••••••"
              />
              <div className="mt-2 text-right">
                <Link
                  data-testid="forgot-password-link"
                  to="/forgot-password"
                  className="text-[11px] text-slate-500 hover:text-[#00CCC9] underline underline-offset-4"
                >
                  Passwort vergessen?
                </Link>
              </div>
            </div>

            {error && (
              <div data-testid="auth-error" className="text-xs text-red-700 bg-red-50 border-l-2 border-red-600 pl-3 py-2">
                {error}
              </div>
            )}

            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className={`w-full ${isTenant ? "text-white" : "text-[#0A0A0A]"} py-4 text-sm font-semibold hover:brightness-105 transition-all disabled:opacity-50 flex items-center justify-center gap-2`}
              style={{ background: isTenant ? (brand?.brand_color || "#0f172a") : "linear-gradient(90deg, #26E0DD 0%, #00CCC9 55%, #009F9C 100%)" }}
            >
              {loading ? "Bitte warten…" : (
                <>
                  Anmelden
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M13 5l7 7-7 7"/>
                  </svg>
                </>
              )}
            </button>
          </form>

          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-[10px] font-bold uppercase tracking-overline text-slate-400">oder</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <button
            data-testid="google-login-button"
            onClick={handleGoogle}
            className="w-full border-2 border-[#0A0A0A] text-[#0A0A0A] py-4 text-sm font-semibold hover:bg-[#0A0A0A] hover:text-white transition-colors flex items-center justify-center gap-3"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.64 9.20455c0-.638-.0573-1.25182-.1636-1.84091H9v3.48136h4.8436c-.2086 1.125-.8427 2.0782-1.7959 2.7164v2.2581h2.9087c1.7018-1.5668 2.6836-3.8741 2.6836-6.61545z" fill="#4285F4"/>
              <path d="M9 18c2.43 0 4.4673-.806 5.9564-2.1805l-2.9087-2.2581c-.806.54-1.8368.8595-3.0477.8595-2.3445 0-4.3282-1.5832-5.036-3.7104H.957v2.3318C2.4382 15.9831 5.4818 18 9 18z" fill="#34A853"/>
              <path d="M3.964 10.71c-.18-.54-.2823-1.1168-.2823-1.71s.1023-1.17.2823-1.71V4.95818H.957C.3477 6.1731 0 7.5477 0 9c0 1.4523.3477 2.8268.957 4.0418L3.964 10.71z" fill="#FBBC05"/>
              <path d="M9 3.57955c1.3214 0 2.5077.45409 3.4405 1.34591l2.5813-2.58131C13.4632.891818 11.4259 0 9 0 5.4818 0 2.4382 2.01682.957 4.95818L3.964 7.29c.7077-2.12727 2.6914-3.71045 5.036-3.71045z" fill="#EA4335"/>
            </svg>
            Mit Google anmelden
          </button>

          {isTenant ? <p className="mt-8 text-[11px] text-slate-500 leading-relaxed" data-testid="login-tenant-note">Anmeldung nur für eingeladene Personen von {brand?.name || "Ihrem Träger"}.{" "}
            <button type="button" onClick={() => { setTenantHint(""); window.location.href = "/login"; }} className="underline underline-offset-2 hover:text-slate-800" data-testid="login-switch-main">Anderer Zugang?</button></p> : (
          <p className="mt-8 text-[11px] text-slate-500 leading-relaxed">
            Anmeldung nur für eingeladene Personen. Kein Konto?{" "}
            <a href="https://we-academy.de" target="_blank" rel="noopener noreferrer" className="text-[#00CCC9] font-semibold underline underline-offset-2 hover:text-[#009F9C]">
              we-academy.de besuchen
            </a>
            .
          </p>)}

          {!isTenant && <div className="mt-6 flex flex-wrap items-center gap-3 text-[10px] font-mono text-slate-400">
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 bg-[#00CCC9]" /> KI Workflows</span>
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 bg-[#0A0A0A]" /> Design</span>
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 bg-[#FFD100]" /> Coaching</span>
            <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 bg-[#A8E6D9]" /> 3D</span>
          </div>}
        </div>
      </div>
    </div>
  );
}

/* ---------- Sub components ---------- */

function BrandTag({ children }) {
  return (
    <span className="px-2 py-1 bg-white/20 backdrop-blur-sm text-[10px] font-mono text-white border border-white/25">
      {children}
    </span>
  );
}
function DarkTag({ children }) {
  return (
    <span className="px-2 py-1 bg-[#0A0A0A]/10 text-[10px] font-mono text-[#0A0A0A] border border-[#0A0A0A]/20">
      {children}
    </span>
  );
}

function StatCard({ k, v, accent = false }) {
  return (
    <div
      className={`relative overflow-hidden border p-4 flex flex-col justify-center ${
        accent
          ? "bg-[#0A0A0A] border-[#0A0A0A] text-[#00CCC9]"
          : "bg-white border-[#0A0A0A]/10 text-[#0A0A0A]"
      }`}
    >
      <div className="font-heading text-3xl font-light tracking-tighter leading-none">{k}</div>
      <div className={`text-[10px] font-mono uppercase tracking-overline mt-1 ${accent ? "text-white/70" : "text-[#0A0A0A]/60"}`}>{v}</div>
    </div>
  );
}

function ArrowUpRight({ dark = false }) {
  return (
    <div
      className={`w-9 h-9 flex items-center justify-center border ${
        dark ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-white text-white"
      } group-hover:rotate-45 transition-transform`}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M7 17L17 7M7 7h10v10" />
      </svg>
    </div>
  );
}

function MobileHero() {
  return (
    <div className="lg:hidden absolute inset-x-0 top-0 h-40 overflow-hidden">
      <div className="absolute inset-0" style={{
        background: "linear-gradient(135deg, #26E0DD 0%, #00CCC9 50%, #A8E6D9 100%)",
      }} />
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: "radial-gradient(circle at 30% 40%, rgba(255,255,255,0.4) 0%, transparent 40%)",
      }} />
    </div>
  );
}
